/**
 * Per-session circuit breaker for a data endpoint (subgraph, price API, ...).
 *
 * Rules (addendum §5): a known-dead endpoint is not awaited again and again; failures are
 * bounded (auto retry after `cooldownMs`), and the UI can offer a manual retry via `reset()`.
 * Loading / unavailable / stale / true zero stay distinct: the breaker only says whether the
 * source is *reachable*; it never produces a value.
 */
export type EndpointState = 'closed' | 'open'

export interface EndpointSnapshot {
  name: string
  state: EndpointState
  failures: number
  lastError?: string
  lastFailureAt?: number
  lastSuccessAt?: number
  openUntil?: number
}

export interface EndpointBreakerOptions {
  name: string
  /** consecutive failures before the breaker opens (default 2) */
  failuresToOpen?: number
  /** how long the breaker stays open before one probe is allowed again (default 60 s) */
  cooldownMs?: number
  /** errors matching one of these are treated as permanent: the breaker opens at once and stays open for the session (until reset()) */
  permanentErrorPatterns?: RegExp[]
}

type Listener = () => void

const DEFAULT_PERMANENT: RegExp[] = [/does not exist/i, /has no field/i, /Cannot query field/i, /indexing_error/i]

/** short, human-readable reason: a GraphQL error message when there is one, never a dumped response object */
export const describeError = (error: unknown): string => {
  const graphQLMessage = (error as any)?.response?.errors?.[0]?.message
  if (typeof graphQLMessage === 'string' && graphQLMessage) return graphQLMessage
  if (error instanceof Error) return error.message.split('\n')[0].split(': {')[0]
  if (typeof error === 'string') return error
  return String(error ?? 'error')
}

export const createEndpointBreaker = (options: EndpointBreakerOptions) => {
  const { name, failuresToOpen = 2, cooldownMs = 60_000, permanentErrorPatterns = DEFAULT_PERMANENT } = options
  let snapshot: EndpointSnapshot = { name, state: 'closed', failures: 0 }
  let permanent = false
  const listeners = new Set<Listener>()

  const emit = () => listeners.forEach((l) => l())
  const set = (patch: Partial<EndpointSnapshot>) => {
    snapshot = { ...snapshot, ...patch }
    emit()
  }

  const isOpen = () => {
    if (snapshot.state !== 'open') return false
    if (permanent) return true
    if (snapshot.openUntil && Date.now() >= snapshot.openUntil) {
      // cooldown elapsed: allow exactly one probe (half-open)
      set({ state: 'closed', openUntil: undefined })
      return false
    }
    return true
  }

  const recordFailure = (error?: unknown) => {
    const message = describeError(error)
    const isPermanent = permanentErrorPatterns.some((re) => re.test(message))
    const failures = snapshot.failures + 1
    if (isPermanent) permanent = true
    const open = isPermanent || failures >= failuresToOpen
    set({
      failures,
      lastError: message.slice(0, 160),
      lastFailureAt: Date.now(),
      state: open ? 'open' : snapshot.state,
      openUntil: open && !isPermanent ? Date.now() + cooldownMs : undefined,
    })
  }

  const recordSuccess = () => {
    permanent = false
    set({ state: 'closed', failures: 0, lastError: undefined, openUntil: undefined, lastSuccessAt: Date.now() })
  }

  /** manual retry from the UI: forget the failure history and probe again */
  const reset = () => {
    permanent = false
    set({ state: 'closed', failures: 0, openUntil: undefined, lastError: undefined })
  }

  const subscribe = (listener: Listener) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  const getSnapshot = () => snapshot

  return { name, isOpen, recordFailure, recordSuccess, reset, subscribe, getSnapshot }
}

export type EndpointBreaker = ReturnType<typeof createEndpointBreaker>

export class EndpointUnavailableError extends Error {
  constructor(name: string, lastError?: string) {
    super(`${name} unavailable${lastError ? `: ${lastError}` : ''}`)
    this.name = 'EndpointUnavailableError'
  }
}
